/**
 * priority-utils.ts — Utilidades de ordenamiento para PrioridadOrden.
 *
 * Funciones puras sin dependencias React.
 * Sincronizado con PRIORIDAD_ORDEN en backend/apps/muestras/constants.py
 */

import type { PrioridadOrden } from "@/hooks/use-procesamiento";

// =============================================================================
// MAPA DE PRIORIDAD NUMÉRICA
// Menor número = mayor prioridad (STAT se muestra primero)
// =============================================================================

const PRIORIDAD_NUMERICA: Record<PrioridadOrden, number> = {
  STAT: 0,
  URGENTE: 1,
  NORMAL: 2,
};

/**
 * Función de comparación para ordenar items por prioridad descendente.
 * STAT > URGENTE > NORMAL
 *
 * Uso: items.sort((a, b) => comparePriority(a.prioridad, b.prioridad))
 */
export function comparePriority(a: PrioridadOrden, b: PrioridadOrden): number {
  return PRIORIDAD_NUMERICA[a] - PRIORIDAD_NUMERICA[b];
}

/**
 * Retorna el valor numérico de una prioridad (menor = más urgente).
 */
export function getPrioridadNumerica(prioridad: PrioridadOrden): number {
  return PRIORIDAD_NUMERICA[prioridad] ?? 2;
}

/**
 * Retorna la clase de color Tailwind para una prioridad.
 *
 * - STAT    → fondo rojo claro
 * - URGENTE → fondo naranja claro
 * - NORMAL  → sin color especial
 */
export function getPrioridadRowClass(prioridad: PrioridadOrden): string {
  switch (prioridad) {
    case "STAT":
      return "bg-red-50 dark:bg-red-950/20";
    case "URGENTE":
      return "bg-orange-50 dark:bg-orange-950/20";
    case "NORMAL":
    default:
      return "";
  }
}

/**
 * Retorna la variante de Badge para una prioridad.
 */
export function getPrioridadBadgeVariant(
  prioridad: PrioridadOrden
): "destructive" | "default" | "secondary" {
  switch (prioridad) {
    case "STAT":
      return "destructive";
    case "URGENTE":
      return "default";
    case "NORMAL":
    default:
      return "secondary";
  }
}

/**
 * Retorna true si la prioridad es urgente o STAT.
 */
export function esUrgente(prioridad: PrioridadOrden): boolean {
  return prioridad === "STAT" || prioridad === "URGENTE";
}
